import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AIPageShell, AIVideo, AIDiagram } from "./DualAIShared";
import ConnectiveAIPage from "./ConnectiveAIPage";
import weddingImage from "./assets/ai-engeeningr/mariage.png";
import ragVideo from "./assets/ai-engeeningr/rag.mp4";
import aiGuardVideo from "./assets/ai-engeeningr/aiguard.mp4";
import generalImage from "./assets/ai-engeeningr/general.png";
import aiProposalImage from "./assets/ai-engeeningr/aiProposal.png";
import connectiveAIImage from "./assets/ai-engeeningr/ConnectiveAI.png";

const architectureLayers = [
  {
    number: "01",
    title: "Context ingestion",
    text: "Briefs, media metadata, slot definitions, user intent, business rules and approved enterprise knowledge are normalized before any model call is executed.",
  },
  {
    number: "02",
    title: "Retrieval and connection",
    text: "Hybrid retrieval, filters and ranking select the smallest relevant context window and connect the current request to project memory, source assets and known business logic.",
  },
  {
    number: "03",
    title: "Model orchestration",
    text: "Task-specific prompts route work to the right model family for proposal generation, visual ideation, prompt editing or grounded answering.",
  },
  {
    number: "04",
    title: "Workflow execution",
    text: "Outputs are converted into usable product artifacts such as proposed Weaves, scene structures, editing instructions, recommendation cards or review decisions.",
  },
  {
    number: "05",
    title: "Guard and observability",
    text: "Confidence, policy, provenance and review state remain attached so the workflow can allow, review or block actions while keeping an auditable trail.",
  },
];

const surfaces = [
  {
    label: "PROPOSAL ENGINE",
    title: "Generate a concept before production starts.",
    text: "Turn a high-level idea into a structured proposal with story angle, target audience, scene logic and suggested capture intent.",
    to: "/use-cases/ai-proposal",
  },
  {
    label: "GENERATION ENGINE",
    title: "Transform intent into usable creative output.",
    text: "Produce text, visual references, template structures and bounded editing prompts that can directly feed the product workflow.",
    to: "/dual-ai/generation",
  },
  {
    label: "GROUNDING & RAG",
    title: "Ground the model in the right evidence.",
    text: "Use retrieval, ranking and source metadata so the model reasons with project knowledge instead of guessing from generic memory.",
    to: "/dual-ai/connexion",
  },
  {
    label: "CONNECTIVE AI",
    title: "Find links between media and propose the Weave.",
    text: "Detect narrative proximity between assets, suggest media improvements and convert discovered connections into a proposed sequence.",
    to: "/dual-ai?view=connective",
  },
  {
    label: "WORKFLOW & GUARDRAILS",
    title: "Keep creation, trust and review inside one system.",
    text: "Coordinate proposal, retrieval, generation, guardrails and human decision points as one operational AI layer.",
    to: "/dual-ai/workflow",
  },
];


const enterpriseArchitecture = [
  ["Tenant-scoped context", "Retrieval and generation should inherit project and tenant boundaries so enterprise data stays isolated before it reaches the model."],
  ["Model abstraction", "Keep product behavior separated from a single model provider so reasoning, vision and generation services can evolve independently."],
  ["Contract-first APIs", "Exchange structured inputs and outputs between AI services and the application so generated results remain predictable and testable."],
  ["Latency & cost budgets", "Use smaller context windows, selective retrieval and task-specific model routing to control response time and inference cost."],
  ["Observability", "Track retrieval quality, model latency, fallback rate and review outcomes so architecture teams can diagnose weak points across the AI path."],
  ["Governance & auditability", "Preserve source provenance, generated-asset lineage and review state so important decisions remain explainable to operators and enterprise customers."],
];

const engineeringSignals = [
  ["Multimodal inputs", "Prompt assembly can mix text instructions, screenshots, media metadata, slot state and retrieved knowledge in the same task."],
  ["Model specialization", "Different tasks can target different models instead of forcing one model to solve ideation, retrieval synthesis and moderation equally well."],
  ["Schema-driven output", "Structured JSON-like outputs reduce UI ambiguity and allow AI results to become product actions rather than decorative text."],
  ["Version separation", "Generated assets remain distinct from uploaded source media so teams always know what is original, derived or synthetic."],
  ["Human checkpoints", "High-impact actions such as publication, payout or sensitive edits can keep explicit review gates without breaking the AI flow."],
  ["Runtime telemetry", "Latency, retrieval quality, confidence and moderation outcomes can be observed to improve the pipeline over time."],
];

export default function DualAIPage() {
  const location = useLocation();
  const view = new URLSearchParams(location.search).get("view");

  if (view === "connective") return <ConnectiveAIPage />;

  return (
    <AIPageShell
      pageTitle="Weavevo AI"
      currentPath="/dual-ai"
      eyebrow="WEAVEVO AI / OVERVIEW"
      heroTitle="Proposal. Generation. Connection. Control."
      heroText="Weavevo AI is not a single chatbot surface. It is a layered production system for living media: propose the story, connect it to the right context, generate structured creative output, and keep policy-aware human control around every critical action."
      hideHeroActions
      finalEyebrow="AI AS AN OPERATING LAYER"
      finalTitle="Design the AI once. Reuse it across the whole media workflow."
      finalText="The strongest AI experience is not a single prompt box. It is a system where proposal, retrieval, generation, recommendation, moderation and review all share the same workflow context."
    >
      <AIDiagram kind="workspace" />

      <section className="dai-generatedVisualStage" aria-label="Weavevo AI orchestration visual">
        <figure className="dai-generatedVisual dai-generatedVisualWide">
          <img src={generalImage} alt="Weavevo multimodal AI orchestration overview" loading="lazy" />
        </figure>
      </section>

      <section className="dai-section dai-aiHubPrinciples">
        <div className="dai-aiHubSectionLead">
          <span className="dai-sectionEyebrow">ARCHITECTURE PRINCIPLES</span>
          <h2>A real AI stack needs separable layers, not one monolithic prompt.</h2>
          <p>
            Weavevo treats AI as an orchestrated set of services. Context preparation, retrieval, generation,
            recommendation and control remain visible and tunable so product teams can improve quality without
            rewriting the entire experience.
          </p>
        </div>

        <div className="dai-aiArchitectureGrid dai-aiArchitectureGridFive">
          {architectureLayers.map((item) => (
            <article key={item.number}>
              <span>{item.number}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-aiSurfaceSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">CAPABILITY MAP</span>
          <h2>Five visible capabilities, one connected engine underneath.</h2>
          <p>
            The user experience can be segmented by purpose: proposing, generating, connecting, orchestrating and
            protecting. Each surface is easier to understand because it maps to a precise workflow function.
          </p>
        </div>
        <div className="dai-aiSurfaceGrid">
          {surfaces.map((item) => (
            <Link key={item.label} className="dai-aiSurfaceCard" to={item.to}>
              <span className="dai-cardLabel">{item.label}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <strong>
                Explore <span aria-hidden="true">→</span>
              </strong>
            </Link>
          ))}
        </div>
      </section>

      <section className="dai-generatedGallery" aria-label="AI capability visuals">
        <figure className="dai-generatedVisual"><img src={aiProposalImage} alt="Proposal engine visual" loading="lazy" /></figure>
        <figure className="dai-generatedVisual"><img src={connectiveAIImage} alt="Connective AI visual" loading="lazy" /></figure>
      </section>

      <section className="dai-section dai-aiHubDeepDive dai-aiHubDeepDiveDark">
        <div className="dai-aiHubDeepDiveInner">
          <div className="dai-aiHubDeepDiveCopy">
            <span className="dai-sectionEyebrow">AI CONNECTION / RAG</span>
            <h2>Retrieve what matters before asking the model to speak.</h2>
            <p>
              Grounded AI starts with disciplined retrieval. Embeddings, hybrid search, metadata filters and reranking
              reduce the search space to the strongest evidence, which improves answer quality and keeps the output tied
              to real project context.
            </p>
            <div className="dai-aiTechChips" aria-label="RAG capabilities">
              <span>Embeddings</span>
              <span>Hybrid retrieval</span>
              <span>Metadata filters</span>
              <span>Reranking</span>
              <span>Source provenance</span>
              <span>Feedback loop</span>
            </div>
            <Link className="dai-aiTextLink" to="/dual-ai/connexion">
              Explore Grounding & RAG <span aria-hidden="true">→</span>
            </Link>
          </div>
          <AIVideo src={ragVideo} label="AI CONNECTION / RAG PIPELINE" />
        </div>
      </section>

      <section className="dai-section dai-aiHubDeepDive dai-aiHubDeepDiveLight">
        <div className="dai-aiHubDeepDiveInner is-reverse">
          <div className="dai-aiHubVisualCard">
            <img src={weddingImage} alt="" loading="lazy" />
            <span>AI GENERATION</span>
          </div>
          <div className="dai-aiHubDeepDiveCopy">
            <span className="dai-sectionEyebrow">AI GENERATION</span>
            <h2>Generate assets that the product can actually use.</h2>
            <p>
              Useful generation produces more than elegant prose. It returns structured proposal blocks, storyline
              variants, scene sequences, visual references and prompt-based editing instructions that the application can
              validate and transform into workflow steps.
            </p>
            <div className="dai-aiTechChips dai-aiTechChipsLight" aria-label="Generative AI capabilities">
              <span>Text</span>
              <span>Images</span>
              <span>Scene planning</span>
              <span>Template proposal</span>
              <span>Prompt editing</span>
              <span>Versioning</span>
            </div>
            <Link className="dai-aiTextLink dai-aiTextLinkDark" to="/dual-ai/generation">
              Explore Generation Engine <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      <AIDiagram kind="architecture" />

      <section className="dai-section dai-aiEngineeringSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">ENGINEERING FOCUS</span>
          <h2>Technical decisions that make the AI system robust in production.</h2>
        </div>
        <div className="dai-aiCapabilityMatrix">
          {engineeringSignals.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-aiEnterpriseSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">ENTERPRISE ARCHITECTURE VIEW</span>
          <h2>What an architecture committee needs to validate before AI reaches production.</h2>
          <p>Beyond model quality, enterprise adoption depends on isolation, portability, predictable interfaces, operational visibility and controllable cost. These concerns belong in the AI architecture from the beginning.</p>
        </div>
        <div className="dai-aiCapabilityMatrix">
          {enterpriseArchitecture.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-aiHubGuardBand">
        <div className="dai-aiHubGuardBandInner">
          <AIVideo src={aiGuardVideo} label="AI WORKFLOW / TRUST LAYER" />
          <div className="dai-aiHubDeepDiveCopy">
            <span className="dai-sectionEyebrow">AI WORKFLOW</span>
            <h2>Trust, moderation and review are part of the AI design itself.</h2>
            <p>
              AI output only becomes operational when the workflow can decide what to do next. Weavevo AI keeps allow,
              review and block pathways around user-submitted or generated media so automation stays compatible with
              enterprise trust requirements.
            </p>
            <Link className="dai-aiTextLink" to="/dual-ai/workflow">
              Explore Workflow & Guardrails <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>
    </AIPageShell>
  );
}
